import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import { Table, Form, Row, Col, Modal, Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_INTERNSHIP_API } from "../../../api/api";

import { IoSearchOutline } from "react-icons/io5";
import { MdEditSquare } from "react-icons/md";
import { FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

// latestIcons
import totalStudents from "../../../assets/icons/admin/enrolled-students.svg";
import totalAppliedInternsh from "../../../assets/icons/admin/total-applied-internship.svg";
import totalApprovedInternsh from "../../../assets/icons/admin/total-approved-internsh.svg";
import totalPendingInternsh from "../../../assets/icons/admin/total-pending-internsh.svg";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";

import { getPageNumbers } from "../../../utils/pagination";
import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import "./AdminInternship.css";

const AdminInternship = () => {
  const navigate = useNavigate();
  const [internships, setInternships] = useState([]);
  const [internshipStats, setInternshipStats] = useState({});

  const [formData, setFormData] = useState({
    internship_id: "",
    projectName: "",
    role: "Internship",
    appliedDate: "",
    startDate: "",
    endDate: "",
    description: "",
    projectFile: null,
  });

  const [showModal, setShowModal] = useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  //loading
  const [loadingAllInterships, setLoadingAllInterships] = useState(true);
  const [updatingLoading, setUpdatingLoading] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const fetchInternships = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingAllInterships(false);
      navigate("/admin-signin");

      return;
    }

    try {
      const response = await axios.get(
        ADMIN_INTERNSHIP_API.GET_TOTAL_INTERNSHIP,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setInternships(data.data || []);
        setInternshipStats(data.stats || {});
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetching interships:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setLoadingAllInterships(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchInternships();
  }, [fetchInternships]);

  // Convert DD-MM-YYYY → YYYY-MM-DD (for date input)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    if (dateString.includes("-") && dateString[4] === "-") {
      return dateString; // already YYYY-MM-DD
    }
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  // Open modal with selected internship
  const handleOpenModal = (internship) => {
    setFormData({
      internship_id: internship.internship_id,
      projectName: internship.project_name || "",
      description: internship.description || "",
      role: "Internship",
      startDate: internship.start_date
        ? formatDateForInput(internship.start_date)
        : "",
      endDate: internship.end_date
        ? formatDateForInput(internship.end_date)
        : "",
      appliedDate: internship.applied_date || "",
      projectFile: null,
    });

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const convertToDateObject = (dateString) => {
    if (!dateString) return null;

    const parts = dateString.split("-");

    // If DD-MM-YYYY
    if (parts[0].length === 2) {
      const [day, month, year] = parts;
      return new Date(`${year}-${month}-${day}`);
    }

    // If already YYYY-MM-DD
    return new Date(dateString);
  };

  const handleUpdateInternship = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setUpdatingLoading(false);
      navigate("/admin-signin");

      return;
    }

    if (!formData.internship_id) {
      showErrorToast("Invalid internship ID");
      setUpdatingLoading(false);
      return;
    }

    // Convert dates
    const appliedDateObj = convertToDateObject(formData.appliedDate);
    const startDateObj = new Date(formData.startDate);
    const endDateObj = formData.endDate ? new Date(formData.endDate) : null;

    // Start date cannot be before applied date
    if (appliedDateObj && startDateObj < appliedDateObj) {
      showErrorToast("Start date cannot be before applied date");
      return;
    }

    // End date cannot be equal to start date
    if (endDateObj && endDateObj.getTime() === startDateObj.getTime()) {
      showErrorToast("End date cannot be same as start date");
      return;
    }

    // End date cannot be before start date
    if (endDateObj && endDateObj < startDateObj) {
      showErrorToast("End date cannot be before start date");
      return;
    }

    // FormData to upload file
    const payload = new FormData();
    payload.append("internship_id", formData.internship_id);
    payload.append("role", formData.role);
    payload.append("status", "Approved");
    payload.append("start_date", formData.startDate);
    if (formData.endDate) payload.append("end_date", formData.endDate);
    if (formData.projectFile)
      payload.append("intership_certificate", formData.projectFile);

    setUpdatingLoading(true);
    try {
      const response = await axios.post(
        ADMIN_INTERNSHIP_API.UPDATE_INTERNSHIP,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        await fetchInternships();
        handleCloseModal();
      } else {
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Update internship error:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/admin-signin");
    } finally {
      setUpdatingLoading(false);
    }
  };

  const handleViewCertificate = (certificateUrl) => {
    if (!certificateUrl) {
      showErrorToast("No certificate available");
      return;
    }
    setSelectedCertificate(certificateUrl);
    setCertificateModalOpen(true);
  };

  const closeCertificateModal = () => {
    setCertificateModalOpen(false);
    setSelectedCertificate(null);
  };

  // Filter & paginate
  const filteredRows = internships.filter(
    (item) =>
      item.student_reg_number
        ?.toString()
        .toLowerCase()
        .includes(search.trim().toLowerCase()) ||
      item.full_name?.toLowerCase().includes(search.trim().toLowerCase()) ||
      item.email?.toLowerCase().includes(search.trim().toLowerCase()),
  );

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } = useSortableTable(filteredRows);

  const handleExportStudentInternship = () => {
    const formattedData = (sortedData || []).map((a) => ({
      Register_ID: a.student_reg_number || "N/A",
      Name: a.full_name || "N/A",
      Email: a.email || "N/A",
      Phone: a.phone_number || "N/A",
      "GitHub URL": a.github_url || "N/A",
      "Project URL": a.web_url || "N/A",
      "Applied Date": a.applied_date || "N/A",
      "Start Date": a.start_date || "N/A",
      "End Date": a.end_date || "N/A",
      Status: a.status || "N/A",
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Student_Internship",
      sheetName: "Internship",
    });
  };

  const rowsPerPage = 5;
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentInternships = sortedData.slice(indexOfFirstRow, indexOfLastRow);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => setCurrentPage(1), [search]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  // function call
  const pages = getPageNumbers(currentPage, totalPages);

  if (loadingAllInterships) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="admin-internshipsss-management-wrapper">
      <h4 className="tutor-feedback-title">Internship Management</h4>
      <p className="tutor-feedback-subtitle">
        Review internship applications and manage internship details.
      </p>

      <Row className="admin-analytics-section-cards-row">
        <Col lg={3} md={4} sm={6} xs={12} className="mb-3">
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Enrolled Students
            </h6>
            <img
              src={totalStudents}
              className="admin-analytics-stat-icon"
              alt="students"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(internshipStats.enrolled_students) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              158 Active • 37 Completed
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Total Applied Internships
            </h6>
            <img
              src={totalAppliedInternsh}
              className="admin-analytics-stat-icon"
              alt="courses"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(internshipStats.total_applied_internships) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              Avg Applied : 50%
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Total Approved Internships
            </h6>
            <img
              src={totalApprovedInternsh}
              className="admin-analytics-stat-icon"
              alt="course"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(internshipStats.total_approved_internships) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              38 Approved • 7 Draft
            </span>
          </div>
        </Col>

        <Col lg={3} md={4} sm={6} xs={12}>
          <div className="admin-analytics-section-stat-card">
            <h6 className="admin-analytics-section-stat-title">
              Total Pending Internships
            </h6>
            <img
              src={totalPendingInternsh}
              className="admin-analytics-stat-icon"
              alt="revenue"
            />

            <h3 className="admin-analytics-section-stat-value">
              {Number(internshipStats.total_pending_internships) || "-"}
            </h3>
            <span className="admin-analytics-section-stat-sub">
              $58K this month
            </span>
          </div>
        </Col>
      </Row>

      <div className="admin-internshipsss-management-filters border-bottom pb-3">
        <Row className="mb-0 admin-internshipsss-management-filters-rowsss justify-content-end">
          <Col md={3}>
            <div className="admin-internshipsss-management-search-box">
              <span className="admin-internshipsss-management-search-icon">
                <IoSearchOutline />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by enroll id, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-internshipsss-management-search-input"
              />
            </div>
          </Col>

          <Col md={1}>
            <Button
              size="sm"
              className="admin-student-management-table-export"
              onClick={handleExportStudentInternship}
              disabled={sortedData.length === 0}
              title={
                sortedData.length === 0
                  ? "No data available to export"
                  : "Export to Excel"
              }
              style={{
                cursor: sortedData.length === 0 ? "not-allowed" : "pointer",
                opacity: sortedData.length === 0 ? 0.5 : 1,
              }}
            >
              <img
                src={excelImg}
                alt="Excel"
                className="admin-student-management-table-export-excel-img"
              />
            </Button>
          </Col>
        </Row>
      </div>

      <div className="admin-internshipsss-management-table-wrapper">
        <Table className="admin-internshipsss-management-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("student_reg_number")}
                style={{ cursor: "pointer" }}
              >
                Enroll Id {renderSortIcon("student_reg_number")}
              </th>
              <th
                onClick={() => handleSort("full_name")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Student {renderSortIcon("full_name")}
                </div>
              </th>
              <th
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer" }}
              >
                Email {renderSortIcon("email")}
              </th>
              <th>Phone No</th>
              <th>GitHub URL</th>
              <th>
                <div className="admin-dashboard-sort-header">Project URL</div>
              </th>
              <th
                onClick={() => handleSort("applied_date")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Applied Date {renderSortIcon("applied_date")}
                </div>
              </th>
              <th
                onClick={() => handleSort("start_date")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  Start Date {renderSortIcon("start_date")}
                </div>
              </th>
              <th
                onClick={() => handleSort("end_date")}
                style={{ cursor: "pointer" }}
              >
                <div className="admin-dashboard-sort-header">
                  End Date {renderSortIcon("end_date")}
                </div>
              </th>
              <th
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer" }}
              >
                Status {renderSortIcon("status")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* {loadingAllInterships ? (
              <tr>
                <td colSpan="12" style={{ textAlign: "center" }}>
                  Loading internships...
                </td>
              </tr>
            ) :  */}
            {currentInternships.length > 0 ? (
              currentInternships.map((intern) => (
                <tr key={intern.internship_id}>
                  <td title={intern.student_reg_number}>
                    {intern.student_reg_number || "-"}
                  </td>
                  <td title={intern.full_name}>{intern.full_name || "-"}</td>
                  <td title={intern.email}>{intern.email || "-"}</td>
                  <td title={intern.phone_number}>
                    {intern.phone_number || "-"}
                  </td>
                  <td>
                    <a
                      href={intern.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Repository
                    </a>
                  </td>

                  <td>
                    <a
                      href={intern.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Live Demo
                    </a>
                  </td>

                  <td>{intern.applied_date || "-"}</td>
                  <td>{intern.start_date || "-"}</td>
                  <td>{intern.end_date || "-"}</td>
                  <td>
                    {" "}
                    <span
                      className={`admin-all-tutor-status ${intern.status?.toLowerCase()}`}
                    >
                      {intern.status || "-"}
                    </span>
                  </td>
                  <td className="admin-internshipsss-management-table-td-buttons">
                    <button
                      className="admin-internshipsss-management-table-view-btn"
                      onClick={() =>
                        handleViewCertificate(intern.intership_certificate)
                      }
                      disabled={!intern.intership_certificate} // disabled if no certificate
                      title={
                        !intern.intership_certificate
                          ? "No Certificate"
                          : "View Certificate"
                      }
                      style={{
                        cursor: intern.intership_certificate
                          ? "pointer"
                          : "not-allowed",
                        opacity: intern.intership_certificate ? 1 : 0.5,
                      }}
                    >
                      <FaEye />
                    </button>
                    <button
                      className="admin-internshipsss-management-table-view-btn"
                      onClick={() => handleOpenModal(intern)}
                    >
                      <MdEditSquare />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="admin-all-courses-empty-table">
                  <img
                    src={Nofound}
                    alt="No Data Found"
                    className="admin-all-tutor-empty-img"
                  />
                  <p>No Data Found</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="tutor-assignment-table-pagination">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>

          {pages.map((page, idx) =>
            page === "..." ? (
              <span key={idx} style={{ padding: "0 3px" }}>
                ...
              </span>
            ) : (
              <Button
                key={idx}
                active={currentPage === page}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </Button>
            ),
          )}

          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        backdrop="static"
        keyboard={false}
        className="admin-internshipsss-management-modal"
      >
        <Modal.Header
          closeButton
          className="admin-internshipsss-management-modal-header-custom"
        >
          <Modal.Title className="admin-internshipsss-management-modal-title-custom">
            Internship Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="admin-internshipsss-management-modal-body-custom">
          <Form className="admin-internshipsss-management-form-custom">
            <Form.Group className="admin-internshipsss-management-form-group-custom mb-3">
              <Form.Label className="admin-internshipsss-management-form-label-custom">
                Project Name
              </Form.Label>
              <Form.Control
                type="text"
                className="admin-internshipsss-management-form-control-custom"
                value={formData.projectName}
                readOnly
              />
            </Form.Group>

            <Form.Group className="admin-internshipsss-management-form-group-custom mb-3">
              <Form.Label className="admin-internshipsss-management-form-label-custom">
                Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                className="admin-internshipsss-management-form-control-custom"
                value={formData.description}
                readOnly
              />
            </Form.Group>

            <Form.Group className="admin-internshipsss-management-form-group-custom mb-3">
              <Form.Label className="admin-internshipsss-management-form-label-custom">
                Role
              </Form.Label>
              <Form.Control
                className="admin-internshipsss-management-form-control-custom"
                value={formData.role}
                readOnly
              />
            </Form.Group>

            <Form.Group className="admin-internshipsss-management-form-group-custom mb-3">
              <Form.Label className="admin-internshipsss-management-form-label-custom">
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                className="admin-internshipsss-management-form-control-custom"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="admin-internshipsss-management-form-group-custom mb-3">
              <Form.Label className="admin-internshipsss-management-form-label-custom">
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                className="admin-internshipsss-management-form-control-custom"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="admin-internshipsss-management-form-group-custom mb-3">
              <Form.Label className="admin-internshipsss-management-form-label-custom">
                Upload Internship File
              </Form.Label>
              <Form.Control
                type="file"
                className="admin-internshipsss-management-form-control-custom"
                onChange={(e) =>
                  setFormData({ ...formData, projectFile: e.target.files[0] })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="admin-internshipsss-management-modal-footer-custom">
          <Button
            onClick={handleCloseModal}
            className="admin-internshipsss-management-btn-close-custom"
          >
            Close
          </Button>
          <Button
            className="admin-internshipsss-management-btn-submit-custom"
            onClick={handleUpdateInternship}
            disabled={updatingLoading}
          >
            {updatingLoading ? "Updating..." : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Certificate Modal using React-Bootstrap */}
      <Modal
        show={certificateModalOpen}
        onHide={closeCertificateModal}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header
          closeButton
          style={{ borderBottom: "none" }}
        ></Modal.Header>

        <Modal.Body style={{ textAlign: "center" }}>
          {selectedCertificate && (
            <iframe
              src={selectedCertificate}
              title="Certificate"
              style={{ width: "100%", height: "500px" }}
              allowFullScreen
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminInternship;
